import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Waves, Loader2, Calendar, Heart, Eye, Navigation } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { API } from '@/App';
import { useGetApp } from '@/context/GetAppContext';
import { OverviewTab, ConditionsTab, HatchesTab, POIsTab } from './river-detail/RiverDetailTabs';

const TABS = ['Overview', 'Conditions', 'Hatches', 'POIs'];

export default function RiverDetailPanel({ gnisId, waterwayData, onClose, onPoiClick }) {
  const [riverData, setRiverData] = useState(null);
  const [poisByType, setPoisByType] = useState({});
  const [totalPoiCount, setTotalPoiCount] = useState(0);
  const [hasCorridor, setHasCorridor] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Overview');
  const [expandedType, setExpandedType] = useState(null);
  const [sheetHeight, setSheetHeight] = useState('half');
  const [nearbyDests, setNearbyDests] = useState([]);
  const dragStartRef = useRef(null);
  const navigate = useNavigate();
  const openGetApp = useGetApp();

  // Conditions tab state
  const [gaugeData, setGaugeData] = useState([]);
  const [gaugeLoading, setGaugeLoading] = useState(false);
  const [expandedChart, setExpandedChart] = useState(null);
  const [chartData, setChartData] = useState({});

  // Load river/lake data
  useEffect(() => {
    if (!gnisId) return;
    setLoading(true);
    // Lakes and rivers have separate endpoints. Route by the type passed in
    // via waterwayData (set upstream in useMapInteractions when the user
    // clicks a lake vs. a stream feature on the map).
    //
    // But: Mapbox tile data can mislabel features (e.g., a lake polygon
    // whose GNIS lives in the `lakes` collection but was hit-tested as a
    // flowline), and the same GNIS can straddle both collections. So if
    // the primary endpoint returns nothing, we transparently fall back to
    // the other one before giving up.
    const primaryIsLake = waterwayData?.type === 'lake';
    const primaryUrl = primaryIsLake
      ? `${API}/lakes/linked/${gnisId}`
      : `${API}/rivers/linked/${gnisId}`;
    const fallbackUrl = primaryIsLake
      ? `${API}/rivers/linked/${gnisId}`
      : `${API}/lakes/linked/${gnisId}`;

    const extractWaterway = (payload) => payload?.river || payload?.lake || null;

    const applyResponse = (d) => {
      const waterway = extractWaterway(d);
      setRiverData(waterway);
      setPoisByType(d.pois_by_type || {});
      setTotalPoiCount(d.total_poi_count || 0);
      // Lakes don't have corridor coords; default to false when absent.
      setHasCorridor(d.has_corridor || false);
      if (waterway?.coordinates) {
        axios.get(`${API}/destinations/nearby`, {
          params: { latitude: waterway.coordinates.lat, longitude: waterway.coordinates.lng, limit: 5, radius: 100 }
        }).then(r => setNearbyDests(r.data || [])).catch(() => {});
      }
    };

    axios.get(primaryUrl)
      .then(res => {
        const waterway = extractWaterway(res.data);
        if (waterway) {
          applyResponse(res.data);
          return null; // signal done
        }
        // Primary had no matching record — try the other collection.
        return axios.get(fallbackUrl).then(fbRes => {
          applyResponse(fbRes.data);
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [gnisId, waterwayData?.type]);

  const river = useMemo(() => riverData || {
    name: waterwayData?.name || waterwayData?.gnis_name || 'Unknown',
    states: [], total_length_mi: null,
  }, [riverData, waterwayData]);
  const displayName = river.display_name || river.name;
  const countyStr = river.county || waterwayData?.county || '';
  const stateStr = river.states?.join(', ') || waterwayData?.state || '';
  const locationStr = countyStr ? `${countyStr}, ${stateStr}` : stateStr;
  const isLake = waterwayData?.type === 'lake';
  const lat = river.coordinates?.lat || waterwayData?.coordinates?.lat;
  const lng = river.coordinates?.lng || waterwayData?.coordinates?.lng;
  const sortedTypes = Object.entries(poisByType).sort((a, b) => b[1].length - a[1].length);

  // Load gauge conditions for Conditions tab
  const loadGaugeConditions = useCallback(async () => {
    const gauges = poisByType.stream_gauge || [];
    if (gauges.length === 0) return;
    setGaugeLoading(true);
    try {
      const results = await Promise.all(
        gauges.map(async (g) => {
          const siteId = g.site_id || g.source_id;
          if (!siteId) return null;
          try {
            const res = await axios.get(`${API}/streamflow/site/${siteId}`);
            return res.data;
          } catch {
            return null;
          }
        })
      );
      setGaugeData(results.filter(Boolean));
    } catch {
    } finally {
      setGaugeLoading(false);
    }
  }, [poisByType]);

  // Lazy-load conditions when tab is selected
  useEffect(() => {
    if (activeTab === 'Conditions' && gaugeData.length === 0 && !gaugeLoading) {
      loadGaugeConditions();
    }
  }, [activeTab, gaugeData.length, gaugeLoading, loadGaugeConditions]);

  // Load sparkline chart for a gauge
  const toggleChart = useCallback(async (siteId) => {
    if (expandedChart === siteId) {
      setExpandedChart(null);
      return;
    }
    setExpandedChart(siteId);
    if (!chartData[siteId]) {
      try {
        const res = await axios.get(`${API}/streamflow/trends/${siteId}`);
        setChartData(prev => ({ ...prev, [siteId]: res.data }));
      } catch {
        setChartData(prev => ({ ...prev, [siteId]: { error: true } }));
      }
    }
  }, [expandedChart, chartData]);

  // Trip itineraries are planned on the free Trip Planner page
  const handleGetItinerary = () => {
    const destination = stateStr ? `${river.name}, ${stateStr}` : river.name;
    const coords = lat && lng ? { latitude: lat, longitude: lng, name: river.name, state: stateStr } : null;
    navigate('/ai-guide', { state: { destination, destinationCoordinates: coords } });
  };

  // Drag to resize
  const handleDragStart = (e) => {
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragStartRef.current = { y: clientY, height: sheetHeight };
  };
  const handleDragEnd = (e) => {
    if (!dragStartRef.current) return;
    const clientY = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
    const delta = clientY - dragStartRef.current.y;
    if (delta > 60) {
      if (sheetHeight === 'full') setSheetHeight('half');
      else onClose();
    } else if (delta < -60) {
      if (sheetHeight === 'half') setSheetHeight('full');
    }
    dragStartRef.current = null;
  };

  const heightClass = sheetHeight === 'full' ? 'h-[85vh]' : 'h-[45vh]';

  return (
    <div className={`absolute bottom-0 left-0 right-0 ${heightClass} bg-white rounded-t-2xl shadow-2xl z-50 flex flex-col transition-all duration-300 ease-out`} data-testid="river-detail-panel">
      {/* Drag handle */}
      <div className="flex justify-center pt-2.5 pb-1 cursor-grab active:cursor-grabbing"
        onMouseDown={handleDragStart} onMouseUp={handleDragEnd}
        onTouchStart={handleDragStart} onTouchEnd={handleDragEnd}
        data-testid="river-panel-drag-handle">
        <div className="w-10 h-1 rounded-full bg-gray-300" />
      </div>

      {/* Header */}
      <div className="px-5 pb-2 flex items-start justify-between flex-shrink-0">
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold text-gray-900 leading-tight truncate" data-testid="river-panel-name">
            {displayName}
          </h2>
          <div className="flex items-center gap-1.5 mt-0.5">
            <Waves className="w-3.5 h-3.5 text-teal-600 flex-shrink-0" />
            <span className="text-sm text-teal-700 font-medium">
              {isLake ? 'Lake' : 'River'}{locationStr && ` \u2022 ${locationStr}`}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 ml-3 flex-shrink-0">
          <button
            onClick={() => openGetApp('Saving rivers and lakes')}
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
            data-testid="river-save-btn"
          >
            <Heart className="w-5 h-5 text-gray-400 hover:text-red-400" />
          </button>
          <button onClick={onClose} className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors" data-testid="river-panel-close">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Action buttons — matching mobile layout */}
      <div className="px-5 pb-2 flex gap-2 flex-shrink-0">
        {lat && lng && (
          <>
            <Button
              size="sm" variant="outline" className="flex-1 text-xs"
              onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank')}
              data-testid="directions-btn"
            >
              <Navigation className="w-3.5 h-3.5 mr-1.5" />
              Directions
            </Button>
            <Button
              size="sm" variant="outline" className="flex-1 text-xs"
              onClick={() => window.open(`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`, '_blank')}
              data-testid="street-view-btn"
            >
              <Eye className="w-3.5 h-3.5 mr-1.5" />
              Street View
            </Button>
          </>
        )}
      </div>
      <div className="px-5 pb-3 flex-shrink-0">
        <Button
          size="sm" className="w-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold"
          onClick={handleGetItinerary}
          data-testid="get-itinerary-btn"
        >
          <Calendar className="w-4 h-4 mr-2" />
          Get Trip Itinerary
        </Button>
      </div>

      {/* Stats bar */}
      {river.total_length_mi && (
        <div className="px-5 pb-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 flex-shrink-0">
          <span><span className="font-semibold text-gray-700">{river.total_length_mi.toFixed(1)}</span> miles</span>
          {river.segment_count > 0 && <span><span className="font-semibold text-gray-700">{river.segment_count}</span> segments</span>}
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 px-5 flex-shrink-0 bg-gray-50/50" data-testid="river-tabs">
        {TABS.map(tab => (
          <button
            key={tab}
            onClick={() => { setActiveTab(tab); if (sheetHeight === 'half') setSheetHeight('full'); }}
            className={`px-3 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === tab ? 'border-orange-500 text-orange-600 bg-white' : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-white'}`}
            data-testid={`tab-${tab.toLowerCase()}`}
          >
            {tab}
            {tab === 'POIs' && totalPoiCount > 0 && (
              <span className="ml-1.5 text-xs font-bold bg-orange-500 text-white rounded-full px-1.5 py-0.5">{totalPoiCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {loading ? (
          <div className="flex items-center justify-center py-12" data-testid="river-panel-loading">
            <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
            <span className="ml-2 text-sm text-gray-500">Loading river data...</span>
          </div>
        ) : (
          <div className="px-5 py-3">
            {activeTab === 'Overview' && <OverviewTab river={river} onUpgrade={() => openGetApp('The full river report')} />}
            {activeTab === 'Conditions' && (
              <ConditionsTab gaugeData={gaugeData} gaugeLoading={gaugeLoading} expandedChart={expandedChart} chartData={chartData} onToggleChart={toggleChart} />
            )}
            {activeTab === 'Hatches' && <HatchesTab hatchChart={river.hatch_chart} proRequired={river.pro_required === true} onUpgrade={() => openGetApp('The full hatch chart')} />}
            {activeTab === 'POIs' && (
              <POIsTab sortedTypes={sortedTypes} totalPoiCount={totalPoiCount} hasCorridor={hasCorridor} expandedType={expandedType} setExpandedType={setExpandedType} onPoiClick={onPoiClick} />
            )}

            {/* Nearby Destinations (all tabs) */}
            {nearbyDests.length > 0 && (
              <div className="mt-6">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Nearby Destinations</h3>
                <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
                  {nearbyDests.map(dest => (
                    <a key={dest.id} href={`/destinations/${dest.slug || dest.id}`}
                      className="flex-shrink-0 w-44 rounded-xl overflow-hidden border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all group"
                      data-testid={`nearby-dest-${dest.id}`}>
                      <div className="h-24 bg-gray-100 overflow-hidden">
                        <img src={dest.image_url || '/placeholder-water.jpg'} alt={dest.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => { e.target.style.display = 'none'; }} />
                      </div>
                      <div className="p-2.5">
                        <p className="text-sm font-medium text-gray-800 truncate">{dest.name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{dest.state} &bull; {dest.distance} mi</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
